import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { toon } from "../toon";
import { cachedShape, mergeFlat } from "./geom";
import { THIGH, type SkaterLook } from "./look";
import { cloth } from "./materials";
import { pelvisSurface, WAIST_Y } from "./trunk";

// The realistic rider's jorts (#75): cut-off denim on the shaped pelvis.
// Legs that flare a little to a frayed hem, their tops tucked up under the
// pelvis; back pockets and belt loops; orange top-stitching round the
// pockets, the front pockets' openings and a J-shaped fly; a copper button.
// Hips space for the pelvis (up +y, front toward -x), hip space for a leg
// (hanging down its -y from the hip joint).

/** Where the toy's cut-offs end, kept: two thirds of the thigh, a little below the hip. */
const HEM_Y = -THIGH * 0.66 + 0.06;
/** The fraying's light cotton, the toy cuff's color. */
const FRAY = "#c9d6ee";
/** Denim's orange-gold top-stitching thread. */
const THREAD = "#d98a2b";

/**
 * A shorts leg lathed round the thigh, bottom to top: the hem, flaring a
 * little wider than the leg above it, up to a top that rounds in over the
 * thigh's (a few millimeters outside it, so no skin shows at the hip) inside
 * the pelvis.
 */
export function jortsLegGeometry(girth: number): THREE.BufferGeometry {
  const profile: [number, number][] = [
    [0.113, HEM_Y],
    [0.11, HEM_Y + 0.04],
    [0.106, -0.08],
    [0.103, -0.04],
    [0.098, -0.01],
    [0.093, 0.02],
    [0.078, 0.045],
    [0.04, 0.07],
    [0.015, 0.078],
  ];
  return new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r * girth, y)),
    28,
  );
}

/** Seeded, so every build of a rider is the same (the rider digest pins it). */
function seeded(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

/**
 * The frayed hem: a thin pale band round the cut, and loose threads hanging
 * from it, 5 to 20 mm long, curling out a little. One mesh, both sides.
 */
export function frayGeometry(girth: number): THREE.BufferGeometry {
  const r = 0.113 * girth;
  const band = new THREE.CylinderGeometry(r + 0.0012, r + 0.0012, 0.008, 28, 1, true);
  band.translate(0, HEM_Y + 0.004, 0);
  const rand = seeded(75);
  const pos: number[] = [];
  const COUNT = 140;
  for (let i = 0; i < COUNT; i++) {
    const a = ((i + rand() * 0.8) / COUNT) * Math.PI * 2;
    const len = 0.005 + rand() * rand() * 0.015;
    const half = 0.0008 + rand() * 0.0006;
    const splay = 0.1 + rand() * 0.35;
    const c = Math.cos(a);
    const s = Math.sin(a);
    // A thread in two segments: straight down from the hem, then curling outward.
    const pts = [0, 0.5, 1].map((t) => {
      const rr = r + 0.0008 + len * splay * t * t;
      return [rr, HEM_Y + 0.002 - len * t] as const;
    });
    for (let k = 0; k < 2; k++) {
      const [r0, y0] = pts[k]!;
      const [r1, y1] = pts[k + 1]!;
      const w0 = half * (1 - k * 0.4);
      const w1 = half * (1 - (k + 1) * 0.4);
      // Across the thread is round the leg (-s, c).
      const q = [
        [r0 * c + s * w0, y0, r0 * s - c * w0],
        [r0 * c - s * w0, y0, r0 * s + c * w0],
        [r1 * c + s * w1, y1, r1 * s - c * w1],
        [r1 * c - s * w1, y1, r1 * s + c * w1],
      ];
      for (const j of [0, 2, 1, 1, 2, 3]) pos.push(...q[j]!);
    }
  }
  const threads = new THREE.BufferGeometry();
  threads.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  threads.setAttribute("uv", new THREE.Float32BufferAttribute(new Array((pos.length / 3) * 2).fill(0.5), 2));
  threads.computeVertexNormals();
  return mergeFlat([band, threads]);
}

/** The fray's material: pale cotton, seen from both sides. */
export function frayMaterial(): THREE.MeshStandardMaterial {
  return cloth(FRAY, true, { side: THREE.DoubleSide });
}

/** The pelvis's surface for a build: x at (y, z), on its back (+x) or front (-x). */
type Surface = (y: number, z: number) => number;

/**
 * A grid over (u across -1..1, v down 0..1) lying `out` off the pelvis's
 * back or front, placed by `at(u, v)` -> [y, z]. Wound to face away from it.
 */
function laid(surface: Surface, back: boolean, cols: number, rows: number, at: (u: number, v: number) => [number, number], out: number): THREE.BufferGeometry {
  const sign = back ? 1 : -1;
  const pos: number[] = [];
  const uv: number[] = [];
  for (let i = 0; i <= cols; i++) {
    const u = -1 + (2 * i) / cols;
    for (let j = 0; j <= rows; j++) {
      const [y, z] = at(u, j / rows);
      pos.push(surface(y, z) + sign * out, y, z);
      uv.push(z / 0.2, y / 0.2);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const a = i * (rows + 1) + j;
      const b = (i + 1) * (rows + 1) + j;
      // (a, a+1, b) faces -x (across +z, down -y); the back flips it.
      if (back) index.push(a, b, a + 1, b, b + 1, a + 1);
      else index.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

/** A stitch line: a thin tube along [y, z] points on the pelvis, `out` off it. */
function seam(surface: Surface, back: boolean, pts: [number, number][], out: number): THREE.BufferGeometry {
  const sign = back ? 1 : -1;
  const path: THREE.Vector3[] = [];
  for (let k = 0; k < pts.length - 1; k++) {
    const [y0, z0] = pts[k]!;
    const [y1, z1] = pts[k + 1]!;
    for (let s = 0; s < 6; s++) {
      const y = y0 + ((y1 - y0) * s) / 6;
      const z = z0 + ((z1 - z0) * s) / 6;
      path.push(new THREE.Vector3(surface(y, z) + sign * out, y, z));
    }
  }
  const [yl, zl] = pts[pts.length - 1]!;
  path.push(new THREE.Vector3(surface(yl, zl) + sign * out, yl, zl));
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(path, false, "centripetal"), path.length * 2, 0.0007, 4, false);
}

/** A back pocket: top edge, sides, and a point at the bottom middle. `side` is +1 or -1. */
const POCKET = { top: 0.025, sides: -0.052, point: -0.07, z: 0.072, half: 0.038, taper: 0.005 };

function pocketAt(side: number, inset = 0): (u: number, v: number) => [number, number] {
  const { top, sides, point, z, half, taper } = POCKET;
  return (u, v) => {
    const bottom = sides + (point - sides) * (1 - Math.abs(u)) + inset;
    return [top - inset + (bottom - (top - inset)) * v, side * z + u * (half - taper * v - inset)];
  };
}

/** The pocket's outline `inset` inside its edge, round from the top corner, as [y, z]. */
function pocketOutline(side: number, inset: number): [number, number][] {
  const at = pocketAt(side, inset);
  return [at(-1, 0), at(-1, 1), at(0, 1), at(1, 1), at(1, 0)];
}

/** The jorts' pelvis details: back pockets and belt loops in the denim, top-stitching, a button. */
export function buildJorts(bottoms: SkaterLook["bottoms"], hips: THREE.Group, width: number): void {
  const details = new THREE.Mesh(cachedShape(`denim-details ${width}`, () => denimDetailsGeometry(width)), cloth(bottoms.color, true));
  details.name = "denim-details";
  hips.add(details);
  const stitching = new THREE.Mesh(cachedShape(`stitching ${width}`, () => stitchingGeometry(width)), toon(THREAD, { roughness: 0.8 }, "cloth"));
  stitching.name = "stitching";
  hips.add(stitching);

  const buttonGeo = new THREE.CylinderGeometry(0.0075, 0.0075, 0.004, 18);
  buttonGeo.rotateZ(Math.PI / 2);
  const button = new THREE.Mesh(buttonGeo, toon("#b87333", { roughness: 0.35, metalness: 0.8 }, "metal"));
  button.name = "button";
  const loopY = (WAIST_Y[0] + WAIST_Y[1]) / 2;
  button.position.set(pelvisSurface(loopY, 0, false, width, true) - 0.003, loopY, 0);
  hips.add(button);
}

/** Back pockets and belt loops, in one geometry. */
function denimDetailsGeometry(width: number): THREE.BufferGeometry {
  const back: Surface = (y, z) => pelvisSurface(y, z, true, width, true);
  const front: Surface = (y, z) => pelvisSurface(y, z, false, width, true);
  const parts: THREE.BufferGeometry[] = [];
  for (const side of [1, -1]) parts.push(laid(back, true, 10, 8, pocketAt(side), 0.0015));
  // Belt loops round the waistband, standing just off it, turned to follow its curve.
  const [y0, y1] = WAIST_Y;
  const loopY = (y0 + y1) / 2;
  for (const [z, onBack] of [[0.1, false], [-0.1, false], [0.08, true], [-0.08, true], [0, true]] as const) {
    const surface = onBack ? back : front;
    const loop = new RoundedBoxGeometry(0.004, y1 - y0 + 0.01, 0.012, 1, 0.0015);
    const slope = (surface(loopY, z + 0.001) - surface(loopY, z - 0.001)) / 0.002;
    loop.rotateY(-Math.atan(slope));
    loop.translate(surface(loopY, z) + (onBack ? 1 : -1) * 0.0025, loopY, z);
    parts.push(loop);
  }
  return mergeFlat(parts);
}

/** The top-stitching: round the back pockets, the front pockets' openings and the fly. */
function stitchingGeometry(width: number): THREE.BufferGeometry {
  const back: Surface = (y, z) => pelvisSurface(y, z, true, width, true);
  const front: Surface = (y, z) => pelvisSurface(y, z, false, width, true);
  const y0 = WAIST_Y[0];
  const threads: THREE.BufferGeometry[] = [];
  for (const side of [1, -1]) {
    // Two rows round each back pocket's sides and bottom, and a curve across it.
    threads.push(seam(back, true, pocketOutline(side, 0.003), 0.0025), seam(back, true, pocketOutline(side, 0.0055), 0.0025));
    const arc = pocketAt(side, 0.008);
    threads.push(seam(back, true, [-1, -0.5, 0, 0.5, 1].map((u) => arc(u, 0.25 + 0.2 * (1 - Math.abs(u)))), 0.0025));
    // The front pockets' openings, curving from the waistband down to the side.
    threads.push(seam(front, false, [[y0, side * 0.085], [y0 - 0.02, side * 0.1], [y0 - 0.045, side * 0.125], [y0 - 0.06, side * 0.145]], 0.001));
  }
  // The J-shaped fly.
  threads.push(seam(front, false, [[y0, 0.028], [-0.02, 0.028], [-0.04, 0.022], [-0.052, 0.008], [-0.055, 0]], 0.001));
  return mergeFlat(threads);
}
